import * as Yup from 'yup'
import { TFunction } from 'i18next'

// Obsolete method as validation is moved to BE
export const getContractChangeFormValidationSchema = (originalSalary: number, originalBonus: number, t: TFunction): Yup.ObjectSchema => {
  return Yup.object().shape({
    changes: Yup.object().shape(
      {
        salary: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'salaryChange',
            // arrow functions cannot be used here because of different behaviour in this binding
            test: function () {
              const { salary, bonus, employeeLevel } = this.parent
              const originalSum: number = originalSalary + originalBonus

              // There should be an error at every field, if no change is defined
              if (salary === undefined && bonus === undefined && employeeLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                    ${t('appraisalMeeting.contract.changes.form.bonus')},
                    ${t('appraisalMeeting.contract.employeeLevel')}`,
                  }),
                  path: 'changes.salary',
                })
              }

              if (!!salary && salary === originalSalary && !bonus) {
                return this.createError({
                  message: t('formError.salaryIsSameAsOriginalSalary'),
                  path: 'changes.salary',
                })
              }

              // There are no rules for pure level changes (and that had to be displayed at the level dropdown)
              if (salary === undefined && bonus === undefined && employeeLevel !== undefined) {
                return true
              }

              // At least one of salary/bonus has a change below here

              // Salary may never be zero
              if (salary === 0) {
                return this.createError({
                  message: t('formError.salaryCannotBeZero', {
                    fieldName: t(`appraisalMeeting.contract.changes.form.salary`),
                    currency: '€',
                  }),
                  path: 'changes.salary',
                })
              }

              // If one of salary/bonus is changed, the new configuration is ok as long as
              // the new sum is greater than or equal to the old sum times the increaseFactor
              const newSalary: number = salary !== undefined ? salary : originalSalary
              const newBonus: number = bonus !== undefined ? bonus : originalBonus
              const newSum: number = newSalary + newBonus

              const salaryReduced: boolean = originalSalary > newSalary
              let increaseFactor = 1.0
              if (salaryReduced) {
                increaseFactor = 1.1
              }
              const expectedSum: number = originalSum * increaseFactor
              if (newSum >= expectedSum) {
                return true
              }

              // Otherwise the change is invalid, however, only show it for the
              // salary-field if the salary was increased (salary decrease is handeled
              // at bonus field)
              if (salary !== undefined && newSalary > originalSalary) {
                return this.createError({
                  message: t('formError.reducedSalaryMinValue', {
                    fieldName: t(`appraisalMeeting.contract.changes.form.salary`),
                    minAmount: (expectedSum - newBonus).toFixed(2),
                    minBonus: (expectedSum - newSalary).toFixed(2),
                    currency: '€',
                  }),
                  path: 'changes.salary',
                })
              }

              return true
            },
          }),
        bonus: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'TargetBonusTest',
            test: function () {
              const { salary, bonus, employeeLevel } = this.parent
              const originalSum: number = originalSalary + originalBonus

              const newSalary: number = salary !== undefined ? salary : originalSalary
              const newBonus: number = bonus !== undefined ? bonus : originalBonus
              const newSum: number = newSalary + newBonus

              const salaryReduced: boolean = originalSalary > newSalary
              let increaseFactor = 1.0
              if (salaryReduced) {
                increaseFactor = 1.1
              }
              const expectedSum: number = originalSum * increaseFactor

              // There should be an error at every field, if no change is defined
              if (salary === undefined && bonus === undefined && employeeLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.bonus')},
                    ${t('appraisalMeeting.contract.changes.form.bonus')},
                    ${t('appraisalMeeting.contract.employeeLevel')}`,
                  }),
                  path: 'changes.bonus',
                })
              }

              // There are no rules for pure level changes (and that had to be displayed at the level dropdown)
              if (salary === undefined && bonus === undefined && employeeLevel !== undefined) {
                return true
              }

              // At least one of salary/bonus has a change below here

              // If one of salary/bonus is changed, the new configuration is ok as long as
              // the new sum is greater than or equal to the old sum times a factor of 1.1
              if (newSum >= expectedSum) {
                return true
              }

              // Otherwise the change is invalid,

              // If the salary was lowered, prompt a special error message at the bonus
              // input-field
              // Show different message if salary was increased or stayed the same
              if (newSalary < originalSalary) {
                const e = this.createError({
                  message: t('formError.reducedSalaryMinBonusValue', {
                    fieldName: t(`appraisalMeeting.contract.changes.form.bonus`),
                    minAmount: (expectedSum - newSalary).toFixed(2),
                    currency: '€',
                  }),
                  path: 'changes.bonus',
                })
                return e
              }

              // If the salary was not lowered, only show the error for the
              // bonus-field if the bonus was changed
              if (bonus !== undefined) {
                return this.createError({
                  message: t('formError.reducedBonusMinValue', {
                    fieldName: t(`appraisalMeeting.contract.changes.form.bonus`),
                    minAmount: (expectedSum - newSalary).toFixed(2),
                    minSalary: (expectedSum - newBonus).toFixed(2),
                    currency: '€',
                  }),
                  path: 'changes.bonus',
                })
              }

              return true
            },
          }),
        employeeLevel: Yup.string().when(['salary', 'bonus'], {
          is: (salary, bonus) => salary === undefined && bonus === undefined,
          then: Yup.string().required(
            t('formError.conditionalRequired', {
              fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                  ${t('appraisalMeeting.contract.changes.form.bonus')},
                  ${t('appraisalMeeting.contract.employeeLevel')}`,
            }),
          ),
        }),
      },
      // the following list of fields is used in the noSoftEdges parameter that is barely documented
      // https://gitmemory.com/issue/jquense/yup/720/564591045
      // it is basically a hack to force a certain sorting of fields to avoid cyclical dependencies
      // it is unclear if it is needed here, it will remain and needs to be analyzed in a different ticket
      [
        ['bonus', 'salary'],
        ['employeeLevel', 'salary'],
        ['employeeLevel', 'bonus'],
      ],
    ),
    validFrom: Yup.string().required(t('formError.required', { fieldName: t(`appraisalMeeting.contract.changes.form.validFrom`) })),
  })
}
